/**
 * Prints the content of a given HTML element in a new tab and tries to preserve
 * the same visual appearance seen on screen.
 * @param {React.RefObject} contentRef - The ref to the HTML element to be printed.
 * @param {string} title - Optional title for the print window.
 */
const printContent = (contentRef, title = "Print") => {
  if (!contentRef?.current) {
    console.error("Invalid content reference.");
    return;
  }

  const markup = contentRef.current.innerHTML;
  const printWindow = window.open("", "_blank", "width=1200,height=900");

  if (!printWindow) {
    console.error("Failed to open print window.");
    return;
  }

  const doc = printWindow.document;

  const customPrintStyles = `
    html, body {
      background: #fff !important;
      margin: 0;
      padding: 0;
      font-family: Arial, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
      font-size: 12px !important;
    }

    body {
      padding: 20px;
      color: #111827;
    }

    .print-root,
    .student-statement-print {
      width: 100%;
    }

    .content-wrapper {
      width: 100%;
      display: flex !important;
      justify-content: space-between !important;
      align-items: flex-start !important;
      gap: 20px !important;
      flex-wrap: nowrap !important;
    }

    .statement-header {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      page-break-after: avoid !important;
      break-after: avoid !important;
      margin-bottom: 18px !important;
    }

    .summary-table {
      min-width: 320px;
      max-width: 360px;
      width: 100%;
    }

    table {
      width: 100%;
      border-collapse: collapse !important;
    }

    th,
    td {
      padding: 10px 12px !important;
      vertical-align: top;
    }

    .table-bordered,
    .table-bordered th,
    .table-bordered td {
      border: 1px solid #dcdcdc !important;
    }

    .table-striped tbody tr:nth-of-type(odd) {
      background-color: #fafafa !important;
    }

    .thead-light th {
      background-color: #f3f4f6 !important;
    }

    .card {
      border: 1px solid #e5e7eb !important;
      box-shadow: none !important;
      page-break-inside: auto !important;
      break-inside: auto !important;
      margin-bottom: 0 !important;
    }

    .card-body {
      padding: 0 !important;
    }

    .transactions-card,
    .transactions-table {
      page-break-inside: auto !important;
      break-inside: auto !important;
    }

    .transactions-table thead {
      display: table-header-group;
    }

    .transactions-table tfoot {
      display: table-footer-group;
    }

    .transactions-table tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }

    .student-photo-box {
      width: 95px !important;
      height: 95px !important;
      min-width: 95px !important;
      border-radius: 16px !important;
      overflow: hidden !important;
      border: 1px solid #dbe1e8 !important;
      background: #f1f5f9 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    .student-photo-box img {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover !important;
      display: block !important;
    }

    .text-right {
      text-align: right !important;
    }

    .text-center {
      text-align: center !important;
    }

    @page {
      size: auto;
      margin: 12mm;
    }

    @media print {
      html, body {
        width: 100%;
        height: auto;
      }

      body {
        padding: 0;
      }

      .statement-header {
        page-break-after: avoid !important;
        break-after: avoid !important;
      }

      .transactions-card {
        page-break-before: auto !important;
        break-before: auto !important;
        margin-top: 0 !important;
      }
    }
  `;

  try {
    doc.open();
    doc.close();

    const html = doc.documentElement;
    const head = doc.head || doc.createElement("head");
    const body = doc.body || doc.createElement("body");

    if (!doc.head) html.appendChild(head);
    if (!doc.body) html.appendChild(body);

    body.innerHTML = "";

    const titleEl = doc.createElement("title");
    titleEl.textContent = title;
    head.appendChild(titleEl);

    Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).forEach((node) => {
      head.appendChild(node.cloneNode(true));
    });

    const styleEl = doc.createElement("style");
    styleEl.textContent = customPrintStyles;
    head.appendChild(styleEl);

    const container = doc.createElement("div");
    container.className = "print-root";
    container.innerHTML = markup;
    body.appendChild(container);

    const waitForImages = () => {
      const images = Array.from(doc.images || []);

      if (!images.length) {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 200);
        return;
      }

      let loaded = 0;
      const done = () => {
        loaded += 1;
        if (loaded >= images.length) {
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
          }, 200);
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          done();
        } else {
          img.onload = done;
          img.onerror = done;
        }
      });
    };

    printWindow.onload = () => {
      setTimeout(waitForImages, 300);
    };

    printWindow.onafterprint = () => {
      printWindow.close();
    };

    setTimeout(() => {
      if (doc.readyState === "complete") {
        waitForImages();
      }
    }, 500);
  } catch (error) {
    console.error("Print failed:", error);
    printWindow.close();
  }
};

export function toUGX(n) {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(Number(n));
  return `${sign}UGX ${abs.toLocaleString("en-UG", { maximumFractionDigits: 0 })}`;
}

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-UG", {
    style: "currency",
    currency: "UGX",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const compactNumberUnsigned = (abs, decimals = 2) => {
  const UNITS = [
    { v: 1e12, s: "T" },
    { v: 1e9, s: "B" },
    { v: 1e6, s: "M" },
    { v: 1e3, s: "K" },
  ];

  for (const u of UNITS) {
    if (abs >= u.v) {
      const val = parseFloat((abs / u.v).toFixed(decimals)).toString();
      return `${val}${u.s}`;
    }
  }

  return abs.toLocaleString("en-UG", { maximumFractionDigits: 0 });
};

export const toUGXCompact = (n, decimals = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "UGX —";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(Number(n));
  return `${sign}UGX ${compactNumberUnsigned(abs, decimals)}`;
};

export const numberFormat = (number, padding = 0) => {
  if (isNaN(number)) {
    return "00";
  }

  const numStr = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (number < 0 && numStr.startsWith("-")) {
    return `(${numStr.slice(1)})`;
  }

  return numStr.padStart(padding, "0");
};

export const formatDate = (dateString) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const hasTime = dateString.includes("T") || dateString.includes(" ");

  if (hasTime) {
    const [datePart, timePart] = dateString.split(/[T ]/);
    const [year, month, day] = datePart.split("-");
    let [hours, minutes] = timePart.split(":");

    const isPM = parseInt(hours, 10) >= 12;
    hours = (parseInt(hours, 10) % 12) || 12;
    const formattedTime = `${hours}:${minutes}${isPM ? "pm" : "am"}`;

    return `${day}-${months[parseInt(month, 10) - 1]}-${year} ${formattedTime}`;
  }

  const [year, month, day] = dateString.split("-");
  return `${day}-${months[parseInt(month, 10) - 1]}-${year}`;
};

export default printContent;